/**
 * DocsPage — Documentation hub for AI Traffic Light for Code.
 * Route: /docs
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Server,
  Code2,
  FileOutput,
  ChevronRight,
  ExternalLink,
  Shield,
  Zap,
  Users,
  Package,
} from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';


type Section = 'getting-started' | 'mcp-server' | 'sdks' | 'api-reference' | 'export-formats';

interface NavItem {
  id: Section;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'getting-started', label: 'Getting Started', icon: BookOpen },
  { id: 'mcp-server', label: 'MCP Server', icon: Server },
  { id: 'sdks', label: 'SDKs', icon: Package },
  { id: 'api-reference', label: 'API Reference', icon: Code2 },
  { id: 'export-formats', label: 'Export Formats', icon: FileOutput },
];

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 overflow-x-auto text-sm">
      <code className="text-zinc-200 font-mono">{children}</code>
    </pre>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xl font-semibold text-white mb-4 mt-8 first:mt-0 flex items-center gap-2">
      {children}
    </h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-base font-semibold text-zinc-100 mb-2 mt-6">{children}</h3>;
}

function Prose({ children }: { children: React.ReactNode }) {
  return <p className="text-zinc-400 leading-relaxed mb-4">{children}</p>;
}

// ── Getting Started ────────────────────────────────────────────────────────────

function GettingStarted() {
  return (
    <div>
      <SectionHeading>
        <BookOpen className="w-5 h-5 text-emerald-400" />
        Getting Started
      </SectionHeading>

      {/* What is AI Traffic Light */}
      <SubHeading>What is AI Traffic Light for Code?</SubHeading>
      <Prose>
        AI Traffic Light for Code is a security scanning platform that uses advanced AI to analyse
        your codebase for vulnerabilities, exposed secrets, licence compliance issues, and AI/ML
        model risks. It surfaces actionable findings with industry-standard scores — AIVSS, CVSS,
        and AARS — so your team can prioritise fixes quickly.
      </Prose>
      <Prose>
        Scans run against GitHub repositories or local file uploads. Results are displayed in
        interactive reports and can be exported to PDF, JSON, or SARIF for integration into your
        existing security workflows.
      </Prose>

      {/* First scan */}
      <SubHeading>Running Your First Scan</SubHeading>
      <ol className="space-y-4 mb-6">
        {[
          {
            step: 1,
            title: 'Create an account',
            desc: 'Sign up at code.aitrafficlight.com. Free tier gives you 5 scans per month with no credit card required.',
          },
          {
            step: 2,
            title: 'Go to the Dashboard',
            desc: 'After logging in you land on the Dashboard. Click "New Scan" or paste a GitHub URL into the scan input.',
          },
          {
            step: 3,
            title: 'Paste a GitHub URL or upload files',
            desc: 'Enter a public or private GitHub repo URL (e.g. https://github.com/org/repo) or drag-and-drop a zip of your project.',
          },
          {
            step: 4,
            title: 'Get your report',
            desc: 'Scans typically complete in under 60 seconds. You\'ll see a full risk report with severity breakdown, affected files, and remediation advice.',
          },
        ].map(({ step, title, desc }) => (
          <li key={step} className="flex gap-4">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 text-sm font-semibold flex items-center justify-center">
              {step}
            </span>
            <div>
              <p className="text-white font-medium mb-0.5">{title}</p>
              <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
            </div>
          </li>
        ))}
      </ol>

      {/* Understanding the report */}
      <SubHeading>Understanding Your Report</SubHeading>
      <Prose>
        Each scan produces a report with four key metrics at the top:
      </Prose>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {[
          {
            label: 'Overall Risk Score',
            color: 'text-red-400',
            bg: 'bg-red-500/10 border-red-500/30',
            desc: 'Composite 0–10 score summarising all findings. Above 7 = critical action required.',
          },
          {
            label: 'AIVSS',
            color: 'text-orange-400',
            bg: 'bg-orange-500/10 border-orange-500/30',
            desc: 'AI Vulnerability Severity Score — measures AI/ML-specific risks like model poisoning and prompt injection.',
          },
          {
            label: 'CVSS',
            color: 'text-yellow-400',
            bg: 'bg-yellow-500/10 border-yellow-500/30',
            desc: 'Common Vulnerability Scoring System — industry-standard score for traditional software vulnerabilities.',
          },
          {
            label: 'AARS',
            color: 'text-blue-400',
            bg: 'bg-blue-500/10 border-blue-500/30',
            desc: 'AI Application Risk Score — holistic risk across the full AI application stack.',
          },
        ].map(({ label, color, bg, desc }) => (
          <div key={label} className={`rounded-lg border p-4 ${bg}`}>
            <p className={`font-semibold text-sm mb-1 ${color}`}>{label}</p>
            <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
      <Prose>
        Findings are grouped by severity: <span className="text-red-400 font-medium">Critical</span>,{' '}
        <span className="text-orange-400 font-medium">High</span>,{' '}
        <span className="text-yellow-400 font-medium">Medium</span>,{' '}
        <span className="text-blue-400 font-medium">Low</span>, and{' '}
        <span className="text-zinc-400 font-medium">Informational</span>. Each finding links to the
        affected file and line number with a plain-English explanation and recommended fix.
      </Prose>

      {/* Subscription tiers */}
      <SubHeading>Subscription Tiers</SubHeading>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-zinc-700">
              <th className="text-left text-zinc-400 font-medium py-3 pr-4 w-1/3">Feature</th>
              {[
                { name: 'Free', color: 'text-zinc-300' },
                { name: 'Personal', price: '$19/mo', color: 'text-blue-400' },
                { name: 'Pro', price: '$49/mo', color: 'text-purple-400' },
                { name: 'Team', price: '$149/mo', color: 'text-emerald-400' },
              ].map(({ name, price, color }) => (
                <th key={name} className={`text-center font-semibold py-3 px-2 ${color}`}>
                  {name}
                  {price && <div className="text-xs font-normal text-zinc-500 mt-0.5">{price}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {[
              ['Scans / month', '5', '50', '200', 'Unlimited'],
              ['Private repos', '—', '✓', '✓', '✓'],
              ['MCP server access', '—', '✓', '✓', '✓'],
              ['PDF / SARIF export', '—', '—', '✓', '✓'],
              ['Team seats', '1', '1', '1', 'Up to 5'],
              ['Compliance crosswalk', '—', '—', '✓', '✓'],
              ['ML / AI security scan', '—', '—', '✓', '✓'],
              ['Priority support', '—', '—', '—', '✓'],
            ].map(([feature, ...values]) => (
              <tr key={feature} className="hover:bg-zinc-800/30 transition-colors">
                <td className="py-3 pr-4 text-zinc-300">{feature}</td>
                {values.map((v, i) => (
                  <td
                    key={i}
                    className={`text-center py-3 px-2 ${
                      v === '✓' ? 'text-emerald-400' : v === '—' ? 'text-zinc-600' : 'text-zinc-300'
                    }`}
                  >
                    {v}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── MCP Server (stub — links to full page) ────────────────────────────────────

function McpServerSection() {
  return (
    <div>
      <SectionHeading>
        <Server className="w-5 h-5 text-emerald-400" />
        MCP Server
      </SectionHeading>
      <Prose>
        AI Traffic Light for Code ships a{' '}
        <span className="text-white font-medium">Model Context Protocol (MCP)</span> server that
        lets AI assistants like Claude use your security scanning tools directly from their chat
        interface — no copy-pasting, no context switching.
      </Prose>
      <Link
        to="/docs/mcp"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600/20 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-600/30 hover:text-emerald-300 transition-colors text-sm font-medium mb-6"
      >
        <Server className="w-4 h-4" />
        View full MCP documentation
        <ChevronRight className="w-4 h-4" />
      </Link>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { icon: Shield, title: '67 tools', desc: 'Risk scans, SBOM, secrets, compliance, and more.' },
          { icon: Zap, title: 'Claude, Cursor, VS Code', desc: 'Works with all major MCP-compatible clients.' },
          { icon: Users, title: 'Auth via device flow', desc: 'One-time browser sign-in. No API keys to manage.' },
        ].map(({ icon: Icon, title, desc }) => (
          <div key={title} className="rounded-lg border border-zinc-700 bg-zinc-800/40 p-4">
            <Icon className="w-5 h-5 text-emerald-400 mb-2" />
            <p className="text-white text-sm font-semibold mb-1">{title}</p>
            <p className="text-zinc-400 text-xs leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SDKs ───────────────────────────────────────────────────────────────────────

function SdksSection() {
  return (
    <div>
      <SectionHeading>
        <Package className="w-5 h-5 text-emerald-400" />
        SDKs
      </SectionHeading>
      <Prose>
        Official client libraries for integrating AI Traffic Light for Code into your applications.
        Both SDKs provide typed, zero-config clients for the full Guardrail API — scanning,
        authentication, billing, organizations, and admin.
      </Prose>

      {/* JavaScript / TypeScript */}
      <SubHeading>JavaScript / TypeScript</SubHeading>
      <Prose>
        Zero-dependency HTTP client for Node.js and the browser. Ships with full TypeScript
        type definitions.
      </Prose>
      <CodeBlock>{`npm install @guardrail-ai/sdk`}</CodeBlock>
      <div className="mt-3" />
      <CodeBlock>{`import { GuardrailClient } from '@guardrail-ai/sdk';

const client = new GuardrailClient(
  'https://api.code.aitrafficlight.com',
  'gr_live_your_api_key'
);

// Run a security scan
const scan = await client.scan(
  'https://github.com/owner/repo',
  'github',
  'standard'
);

// Check user info
const me = await client.me();
console.log(me.tier, me.email);`}</CodeBlock>
      <div className="mt-2" />
      <a
        href="https://github.com/GT-Traffic-Light/guardrail-sdk-js"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        View on GitHub
      </a>

      {/* Python */}
      <SubHeading>Python</SubHeading>
      <Prose>
        Async HTTP client built on <code className="text-emerald-400 bg-zinc-800 px-1 py-0.5 rounded text-xs">httpx</code>.
        Supports Python 3.9+.
      </Prose>
      <CodeBlock>{`pip install guardrail-ai`}</CodeBlock>
      <div className="mt-3" />
      <CodeBlock>{`from guardrail import GuardrailClient

client = GuardrailClient(
    base_url="https://api.code.aitrafficlight.com",
    token="gr_live_your_api_key"
)

# Run a security scan
scan = await client.scan(
    target="https://github.com/owner/repo",
    scan_type="github",
    depth="standard"
)

# Check user info
me = await client.me()
print(me["tier"], me["email"])`}</CodeBlock>
      <div className="mt-2" />
      <a
        href="https://github.com/GT-Traffic-Light/guardrail-sdk-python"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm font-medium transition-colors"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        View on GitHub
      </a>

      {/* Authentication */}
      <SubHeading>Authentication</SubHeading>
      <Prose>Both SDKs support multiple authentication methods:</Prose>
      <div className="space-y-2 mt-2 mb-4">
        {[
          { method: 'API Key', desc: 'Pass a gr_live_… key at initialization. Best for server-to-server.' },
          { method: 'Magic Link', desc: 'Request a sign-in email, then redeem the token for JWTs.' },
          { method: 'PKCE Flow', desc: 'Full OAuth 2.0 flow with Keycloak. Best for browser apps.' },
          { method: 'Device Flow', desc: 'For CLIs and MCP servers. Opens browser for one-time sign-in.' },
        ].map(({ method, desc }) => (
          <div key={method} className="flex gap-3 items-start">
            <span className="text-emerald-400 text-sm font-mono font-semibold whitespace-nowrap">{method}</span>
            <span className="text-zinc-400 text-sm">{desc}</span>
          </div>
        ))}
      </div>

      {/* Available methods */}
      <SubHeading>Key Methods</SubHeading>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-zinc-700">
              <th className="text-left py-2 pr-4 text-zinc-300 font-semibold">Method</th>
              <th className="text-left py-2 text-zinc-300 font-semibold">Description</th>
            </tr>
          </thead>
          <tbody className="text-zinc-400">
            {[
              ['health()', 'System health check'],
              ['onboard(name, email)', 'Register a new user account'],
              ['me()', 'Get current user info'],
              ['scan(target, type, depth)', 'Initiate a security scan'],
              ['getScan(scanId)', 'Get scan status / result'],
              ['getScanHistory()', 'Paginated scan history'],
              ['getSubscription()', 'Live subscription state'],
              ['guestCheckout(payload)', 'Start a Stripe checkout session'],
              ['listKeys()', 'List API keys'],
              ['createKey()', 'Generate a new API key'],
              ['listOrganizations()', 'List orgs the caller belongs to'],
              ['getPricing()', 'Tier pricing and metadata'],
            ].map(([method, desc]) => (
              <tr key={method} className="border-b border-zinc-800">
                <td className="py-2 pr-4 font-mono text-emerald-400 whitespace-nowrap">{method}</td>
                <td className="py-2">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── API Reference ──────────────────────────────────────────────────────────────

function ApiReference() {
  return (
    <div>
      <SectionHeading>
        <Code2 className="w-5 h-5 text-emerald-400" />
        API Reference
      </SectionHeading>
      <Prose>
        The REST API powers all scans, reports, and account operations. Authentication uses a
        Bearer token obtained from your account settings.
      </Prose>

      <SubHeading>Base URLs</SubHeading>
      <div className="space-y-2 mb-6">
        <div className="flex items-center gap-3 text-sm">
          <code className="text-emerald-400 font-mono">https://mcp.code.aitrafficlight.com</code>
        </div>
      </div>

      <SubHeading>Authentication</SubHeading>
      <CodeBlock>{`Authorization: Bearer <your-api-token>`}</CodeBlock>
      <Prose>
        Generate an API token from <strong className="text-white">Account → API Keys</strong>. Tokens
        inherit your subscription tier limits.
      </Prose>

      <SubHeading>Key Endpoints</SubHeading>
      <div className="space-y-3 mb-6">
        {[
          { method: 'POST', path: '/v1/scan/risk-report', desc: 'Run a full risk report on a GitHub URL or file.' },
          { method: 'POST', path: '/v1/scan/secrets', desc: 'Detect exposed secrets and credentials.' },
          { method: 'POST', path: '/v1/scan/dependencies', desc: 'Audit dependencies for known CVEs.' },
          { method: 'POST', path: '/v1/scan/sbom', desc: 'Generate a Software Bill of Materials.' },
          { method: 'GET', path: '/v1/reports', desc: 'List your past scan reports.' },
          { method: 'GET', path: '/v1/reports/:id', desc: 'Fetch a specific report by ID.' },
        ].map(({ method, path, desc }) => (
          <div key={path} className="flex items-start gap-3 text-sm border border-zinc-800 rounded-lg p-3 hover:border-zinc-700 transition-colors">
            <span className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-bold font-mono ${
              method === 'GET' ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'
            }`}>
              {method}
            </span>
            <div>
              <code className="text-zinc-200 font-mono text-xs">{path}</code>
              <p className="text-zinc-500 mt-0.5 text-xs">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-zinc-700 bg-zinc-800/30 p-4 flex items-start gap-3">
        <ExternalLink className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-zinc-300 text-sm font-medium mb-0.5">Full API docs coming soon</p>
          <p className="text-zinc-500 text-xs">
            Interactive OpenAPI / Swagger documentation is under development. In the meantime, use
            the MCP server for AI-native integrations or contact support.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Export Formats ────────────────────────────────────────────────────────────

function ExportFormats() {
  return (
    <div>
      <SectionHeading>
        <FileOutput className="w-5 h-5 text-emerald-400" />
        Export Formats
      </SectionHeading>
      <Prose>
        Reports can be exported in multiple formats to fit your security workflow. Export options
        are available on the individual report page.
      </Prose>

      <div className="space-y-4 mb-6">
        {[
          {
            format: 'PDF',
            tier: 'Pro+',
            color: 'text-red-400',
            desc: 'Polished, printable report with your scan summary, score breakdown, and all findings. Ideal for sharing with stakeholders or auditors.',
          },
          {
            format: 'JSON',
            tier: 'Personal+',
            color: 'text-blue-400',
            desc: 'Machine-readable export of the full report data. Use this to pipe results into your own dashboards, ticketing systems, or CI pipelines.',
          },
          {
            format: 'SARIF',
            tier: 'Pro+',
            color: 'text-purple-400',
            desc: 'Static Analysis Results Interchange Format. Upload directly to GitHub Code Scanning, Azure DevOps, or any SARIF-compatible security tool.',
          },
          {
            format: 'CSV',
            tier: 'Personal+',
            color: 'text-emerald-400',
            desc: 'Flat file listing all findings with severity, affected path, and description. Useful for spreadsheet-based tracking or simple imports.',
          },
        ].map(({ format, tier, color, desc }) => (
          <div key={format} className="rounded-lg border border-zinc-700 bg-zinc-800/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-base font-bold font-mono ${color}`}>.{format.toLowerCase()}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-400">{tier}</span>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>

      <SubHeading>Business Summary Export</SubHeading>
      <Prose>
        Pro and Team subscribers can also generate a <span className="text-white font-medium">Business Summary</span> —
        a non-technical executive overview that translates security findings into business risk
        language. Accessible from the report page via the "Business Summary" button.
      </Prose>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const SECTION_COMPONENTS: Record<Section, React.ComponentType> = {
  'getting-started': GettingStarted,
  'mcp-server': McpServerSection,
  'sdks': SdksSection,
  'api-reference': ApiReference,
  'export-formats': ExportFormats,
};

export default function DocsPage() {
  useDocumentTitle('Documentation');
  const [activeSection, setActiveSection] = useState<Section>('getting-started');
  const ActiveComponent = SECTION_COMPONENTS[activeSection];

  return (
    <div className="min-h-screen bg-[rgb(5,5,5)] flex flex-col">
      <AppHeader />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-6xl">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Documentation</h1>
          <p className="text-zinc-400">
            Everything you need to get started with AI Traffic Light for Code.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <aside className="md:w-52 flex-shrink-0">
            <nav className="space-y-1 md:sticky md:top-24">
              {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveSection(id)}
                  className={[
                    'w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-left transition-colors',
                    activeSection === id
                      ? 'bg-zinc-700 text-white'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800',
                  ].join(' ')}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${activeSection === id ? 'text-emerald-400' : ''}`} />
                  {label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6 md:p-8">
              <ActiveComponent />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
